<template>
  <transition name="accruals-sheet">
  <div
    v-if="visible && panelData"
    class="accruals-estimate-detail-panel"
    data-testid="accruals-estimate-detail-panel"
  >
    <aside
      ref="sheetBody"
      class="accruals-estimate-detail-panel__panel"
      role="dialog"
      aria-modal="false"
      aria-label="Estimate details"
      data-testid="accruals-estimate-detail-panel__drawer"
      @keydown="trapTabKey"
    >
          <header class="accruals-estimate-detail-panel__header">
            <div class="accruals-estimate-detail-panel__title-group">
              <h2 class="accruals-estimate-detail-panel__title">
                {{ panelData.vendor || 'Estimate' }}
              </h2>
              <div class="accruals-estimate-detail-panel__meta-row">
                <span
                  v-if="panelData.poNumber"
                  class="accruals-estimate-detail-panel__po-tag"
                  :class="{
                    'accruals-estimate-detail-panel__po-tag--vendor': isMutedHeaderRef,
                  }"
                  data-testid="accruals-estimate-detail-panel__po-tag"
                >
                  {{ panelData.poNumber }}
                </span>
                <span
                  v-if="statusKey"
                  class="accruals-estimate-detail-panel__status"
                  :class="`accruals-estimate-detail-panel__status--${statusKey}`"
                  data-testid="accruals-estimate-detail-panel__status-indicator"
                >
                  <span
                    v-if="statusKey !== 'excluded'"
                    class="accruals-estimate-detail-panel__status-dot"
                    aria-hidden="true"
                  />
                  {{ statusLabel(statusKey) }}
                </span>
                <span
                  v-if="headerLocation"
                  class="accruals-estimate-detail-panel__meta-caption"
                  data-testid="accruals-estimate-detail-panel__location"
                  :title="headerLocation"
                >
                  {{ headerLocation }}
                </span>
                <span
                  v-if="headerCaption"
                  class="accruals-estimate-detail-panel__meta-caption"
                  data-testid="accruals-estimate-detail-panel__description"
                >
                  {{ headerCaption }}
                </span>
              </div>
            </div>
            <div class="accruals-estimate-detail-panel__header-actions">
              <div
                v-if="showIncludeAction"
                class="accruals-estimate-detail-panel__exclude-anchor"
              >
                <button
                  type="button"
                  class="accruals-estimate-detail-panel__exclude-btn"
                  data-testid="accruals-estimate-detail-panel__include-btn"
                  @click="openIncludePopover"
                >
                  Include in cycle
                </button>
                <div
                  v-if="includePopoverOpen"
                  class="accruals-estimate-detail-panel__exclude-popover"
                  role="dialog"
                  aria-label="Include in cycle"
                  data-testid="accruals-estimate-detail-panel__include-popover"
                >
                  <p class="accruals-estimate-detail-panel__exclude-title">Include in cycle</p>
                  <p class="accruals-estimate-detail-panel__exclude-help">
                    This line returns to pending submission. Tell the team why.
                  </p>
                  <label
                    class="accruals-estimate-detail-panel__exclude-label"
                    for="accruals-estimate-detail-panel__include-reason"
                  >
                    Remarks*
                  </label>
                  <textarea
                    id="accruals-estimate-detail-panel__include-reason"
                    v-model="includeReason"
                    class="accruals-estimate-detail-panel__exclude-input"
                    rows="4"
                    placeholder="Why are you including this line?"
                    data-testid="accruals-estimate-detail-panel__include-reason"
                  />
                  <p
                    v-if="includeError"
                    class="accruals-estimate-detail-panel__exclude-error"
                    role="alert"
                  >
                    {{ includeError }}
                  </p>
                  <div class="accruals-estimate-detail-panel__exclude-footer">
                    <button
                      type="button"
                      class="accruals-estimate-detail-panel__exclude-confirm"
                      data-testid="accruals-estimate-detail-panel__include-confirm"
                      @click="confirmInclude"
                    >
                      Yes, include
                    </button>
                  </div>
                </div>
              </div>
              <div
                v-else-if="showExcludeAction"
                class="accruals-estimate-detail-panel__exclude-anchor"
              >
                <button
                  type="button"
                  class="accruals-estimate-detail-panel__exclude-btn"
                  data-testid="accruals-estimate-detail-panel__exclude-btn"
                  @click="openExcludePopover"
                >
                  Exclude from cycle
                </button>
                <div
                  v-if="excludePopoverOpen"
                  class="accruals-estimate-detail-panel__exclude-popover"
                  role="dialog"
                  aria-label="Exclude from cycle"
                  data-testid="accruals-estimate-detail-panel__exclude-popover"
                >
                  <p class="accruals-estimate-detail-panel__exclude-title">Exclude from cycle</p>
                  <p class="accruals-estimate-detail-panel__exclude-help">
                    This line will not post to the schedule. Tell the team why.
                  </p>
                  <label
                    class="accruals-estimate-detail-panel__exclude-label"
                    for="accruals-estimate-detail-panel__exclude-reason"
                  >
                    Remarks*
                  </label>
                  <textarea
                    id="accruals-estimate-detail-panel__exclude-reason"
                    v-model="excludeReason"
                    class="accruals-estimate-detail-panel__exclude-input"
                    rows="4"
                    placeholder="Why are you excluding this line?"
                    data-testid="accruals-estimate-detail-panel__exclude-reason"
                  />
                  <p
                    v-if="excludeError"
                    class="accruals-estimate-detail-panel__exclude-error"
                    role="alert"
                  >
                    {{ excludeError }}
                  </p>
                  <div class="accruals-estimate-detail-panel__exclude-footer">
                    <button
                      type="button"
                      class="accruals-estimate-detail-panel__exclude-confirm"
                      data-testid="accruals-estimate-detail-panel__exclude-confirm"
                      @click="confirmExclude"
                    >
                      Yes, exclude
                    </button>
                  </div>
                </div>
              </div>
              <button
                type="button"
                class="accruals-estimate-detail-panel__close-btn"
                aria-label="Close panel"
                data-testid="accruals-estimate-detail-panel__close-btn"
                @click="onClose"
              >
                <img
                  :src="assets.ICON_CLOSE"
                  alt=""
                  class="accruals-estimate-detail-panel__close-icon"
                />
              </button>
            </div>
          </header>

    <div class="accruals-estimate-detail-panel__tabs" data-testid="accruals-estimate-detail-panel__tabs">
      <button
        v-for="tab in detailTabs"
        :key="tab.id"
        type="button"
        class="accruals-estimate-detail-panel__tab"
        :class="{ 'accruals-estimate-detail-panel__tab--active': activeTab === tab.id }"
        :data-testid="`accruals-estimate-detail-panel__tab--${tab.id}`"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}
        <span
          v-if="tab.count != null"
          class="accruals-estimate-detail-panel__tab-count"
          :data-testid="`accruals-estimate-detail-panel__tab-count--${tab.id}`"
        >
          {{ tab.count }}
        </span>
      </button>
    </div>

    <div
      class="accruals-estimate-detail-panel__body"
      :class="{ 'accruals-estimate-detail-panel__body--fill': activeTab !== 'details' }"
    >
      <div
        v-if="showShimmer"
        class="accruals-estimate-detail-panel__shimmer"
        data-testid="accruals-estimate-detail-panel__shimmer"
        aria-hidden="true"
      >
        <span
          v-for="slot in 5"
          :key="slot"
          class="accruals-estimate-detail-panel__shimmer-block"
        />
      </div>
      <template v-else-if="activeTab === 'details'">
          <section
            v-if="isPreparerDecision"
            class="accruals-estimate-detail-panel__decision"
            data-testid="accruals-estimate-detail-panel__preparer-decision"
          >
            <div class="accruals-estimate-detail-panel__decision-head">
              <h3 class="accruals-estimate-detail-panel__decision-title">Estimate</h3>
              <base-button
                primary
                small
                class="shadow-off"
                data-testid="accruals-estimate-detail-panel__submit-estimate"
                @click="onApprove"
              >
                Submit estimate
              </base-button>
            </div>
            <div class="accruals-estimate-detail-panel__decision-options">
              <button
                v-if="hasRuleEstimate"
                type="button"
                class="accruals-estimate-detail-panel__decision-option"
                :class="{ 'accruals-estimate-detail-panel__decision-option--selected': selectedAmountOption === amountOptions.RULE }"
                data-testid="accruals-estimate-detail-panel__option-rule"
                @click="onSelectAmountOption(amountOptions.RULE)"
              >
                <span class="accruals-estimate-detail-panel__decision-radio" aria-hidden="true" />
                <span class="accruals-estimate-detail-panel__decision-option-copy">
                  <span class="accruals-estimate-detail-panel__decision-option-label">Use rule</span>
                  <span class="accruals-estimate-detail-panel__decision-option-amount">
                    {{ formatCurrency(panelData.ruleEstimate) }}
                  </span>
                </span>
              </button>
              <button
                v-if="hasNoaEstimate"
                type="button"
                class="accruals-estimate-detail-panel__decision-option"
                :class="{
                  'accruals-estimate-detail-panel__decision-option--selected': selectedAmountOption === amountOptions.NOA,
                }"
                data-testid="accruals-estimate-detail-panel__option-noa"
                @click="onSelectAmountOption(amountOptions.NOA)"
              >
                <span class="accruals-estimate-detail-panel__decision-radio" aria-hidden="true" />
                <span class="accruals-estimate-detail-panel__decision-option-copy">
                  <span class="accruals-estimate-detail-panel__decision-option-label-row">
                    <span class="accruals-estimate-detail-panel__decision-option-label accruals-estimate-detail-panel__decision-option-label--accent">Use Noa</span>
                    <span
                      v-if="suggestedOption === amountOptions.NOA && !noaOptionDisabled"
                      class="accruals-estimate-detail-panel__decision-suggested"
                    >SUGGESTED</span>
                  </span>
                  <span class="accruals-estimate-detail-panel__decision-option-amount">
                    {{ formatCurrency(panelData.noaEstimate) }}
                  </span>
                </span>
              </button>
              <button
                type="button"
                class="accruals-estimate-detail-panel__decision-option"
                :class="{ 'accruals-estimate-detail-panel__decision-option--selected': selectedAmountOption === amountOptions.CUSTOM }"
                data-testid="accruals-estimate-detail-panel__option-custom"
                @click="onSelectAmountOption(amountOptions.CUSTOM)"
              >
                <span class="accruals-estimate-detail-panel__decision-radio" aria-hidden="true" />
                <span class="accruals-estimate-detail-panel__decision-option-copy">
                  <span class="accruals-estimate-detail-panel__decision-option-label">Custom Estimate</span>
                  <input
                    v-if="selectedAmountOption === amountOptions.CUSTOM"
                    v-model="customAmount"
                    type="text"
                    class="accruals-estimate-detail-panel__decision-custom"
                    placeholder="e.g., 500,000.00"
                    data-testid="accruals-estimate-detail-panel__custom-amount-input"
                    @click.stop
                  />
                  <span
                    v-else
                    class="accruals-estimate-detail-panel__decision-placeholder"
                  >e.g., 500,000.00</span>
                </span>
              </button>
            </div>
            <p
              v-if="submitErrors.amount"
              class="accruals-estimate-detail-panel__field-error"
              role="alert"
            >
              {{ submitErrors.amount }}
            </p>
            <div class="accruals-estimate-detail-panel__decision-gl">
              <div class="accruals-estimate-detail-panel__decision-field">
                <span class="accruals-estimate-detail-panel__decision-field-label">Debit GL</span>
                <lb-dropdown
                  :value="debitGl"
                  :items="glDropdownItems"
                  :hidedetails="!showGlErrors || !glValidation.debit"
                  :error="showGlErrors ? glValidation.debit : ''"
                  label=""
                  searchable
                  placeholder="Select GL account..."
                  data-testid="accruals-estimate-detail-panel__debit-gl"
                  @input="debitGl = $event"
                />
                <p
                  v-if="submitErrors.debit"
                  class="accruals-estimate-detail-panel__field-error"
                  role="alert"
                >
                  {{ submitErrors.debit }}
                </p>
              </div>
              <div class="accruals-estimate-detail-panel__decision-field">
                <span class="accruals-estimate-detail-panel__decision-field-label">Credit GL</span>
                <lb-dropdown
                  :value="creditGl"
                  :items="glDropdownItems"
                  :hidedetails="!showGlErrors || !glValidation.credit"
                  :error="showGlErrors ? glValidation.credit : ''"
                  label=""
                  searchable
                  placeholder="Select GL account..."
                  data-testid="accruals-estimate-detail-panel__credit-gl"
                  @input="creditGl = $event"
                />
                <p
                  v-if="submitErrors.credit"
                  class="accruals-estimate-detail-panel__field-error"
                  role="alert"
                >
                  {{ submitErrors.credit }}
                </p>
              </div>
            </div>
            <label class="accruals-estimate-detail-panel__decision-field">
              <span class="accruals-estimate-detail-panel__decision-field-label">
                Note to approver (saved to Comments)
              </span>
              <input
                v-model="noteToApprover"
                type="text"
                class="accruals-estimate-detail-panel__decision-note"
                :class="{ 'accruals-estimate-detail-panel__decision-note--error': Boolean(submitErrors.note) }"
                placeholder="Add a note for the approver…"
                data-testid="accruals-estimate-detail-panel__note-input"
              />
              <p
                v-if="submitErrors.note"
                class="accruals-estimate-detail-panel__field-error"
                role="alert"
              >
                {{ submitErrors.note }}
              </p>
            </label>
          </section>

          <section
            v-else-if="isApproverDecision"
            class="accruals-estimate-detail-panel__decision"
            data-testid="accruals-estimate-detail-panel__approver-decision"
          >
            <div class="accruals-estimate-detail-panel__decision-head">
              <h3 class="accruals-estimate-detail-panel__decision-title">Confirmed estimate</h3>
              <div
                v-if="canApprove"
                class="accruals-estimate-detail-panel__decision-actions"
              >
                <div class="accruals-estimate-detail-panel__exclude-anchor">
                  <button
                    type="button"
                    class="accruals-estimate-detail-panel__decision-reject"
                    data-testid="accruals-estimate-detail-panel__reject"
                    @click="openRejectPopover"
                  >
                    Reject
                  </button>
                  <div
                    v-if="rejectPopoverOpen"
                    class="accruals-estimate-detail-panel__exclude-popover"
                    role="dialog"
                    aria-label="Reject estimate"
                    data-testid="accruals-estimate-detail-panel__reject-popover"
                  >
                    <p class="accruals-estimate-detail-panel__exclude-title">Reject estimate</p>
                    <p class="accruals-estimate-detail-panel__exclude-help">
                      Let the preparer know what needs to be corrected.
                    </p>
                    <label
                      class="accruals-estimate-detail-panel__exclude-label"
                      for="accruals-estimate-detail-panel__reject-reason"
                    >
                      Remarks*
                    </label>
                    <textarea
                      id="accruals-estimate-detail-panel__reject-reason"
                      v-model="reasonText"
                      class="accruals-estimate-detail-panel__exclude-input"
                      :class="{ 'accruals-estimate-detail-panel__exclude-input--error': Boolean(reasonError) }"
                      rows="4"
                      data-testid="accruals-estimate-detail-panel__reject-reason"
                    />
                    <p
                      v-if="reasonError"
                      class="accruals-estimate-detail-panel__field-error"
                      role="alert"
                    >
                      {{ reasonError }}
                    </p>
                    <div class="accruals-estimate-detail-panel__exclude-footer">
                      <button
                        type="button"
                        class="accruals-estimate-detail-panel__exclude-confirm"
                        :class="{ 'accruals-estimate-detail-panel__exclude-confirm--disabled': !reasonText.trim() }"
                        :disabled="!reasonText.trim()"
                        data-testid="accruals-estimate-detail-panel__reject-confirm"
                        @click="confirmReason"
                      >
                        Submit
                      </button>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  class="accruals-estimate-detail-panel__decision-approve"
                  data-testid="accruals-estimate-detail-panel__approve"
                  @click="onFooterAction('approve')"
                >
                  Approve
                </button>
              </div>
            </div>
            <div class="accruals-estimate-detail-panel__decision-rows">
              <div class="accruals-estimate-detail-panel__decision-row">
                <span>Final amount</span>
                <span class="accruals-estimate-detail-panel__decision-row-value">
                  <span
                    v-if="amountSourceTag"
                    class="accruals-estimate-detail-panel__decision-source"
                  >{{ amountSourceTag }}</span>
                  {{ formatCurrency(confirmedAmount) }}
                </span>
              </div>
              <div class="accruals-estimate-detail-panel__decision-row">
                <span>Debit GL</span>
                <span class="accruals-estimate-detail-panel__decision-row-value">{{ glAccountLabel(debitGl) }}</span>
              </div>
              <div class="accruals-estimate-detail-panel__decision-row">
                <span>Credit GL</span>
                <span class="accruals-estimate-detail-panel__decision-row-value">{{ glAccountLabel(creditGl) }}</span>
              </div>
            </div>
          </section>
          <section
            v-else-if="isApprovedDecision"
            class="accruals-estimate-detail-panel__decision"
            data-testid="accruals-estimate-detail-panel__approved-decision"
          >
            <div class="accruals-estimate-detail-panel__decision-head">
              <h3 class="accruals-estimate-detail-panel__decision-title">Approved estimate</h3>
              <div class="accruals-estimate-detail-panel__exclude-anchor">
                <button
                  type="button"
                  class="accruals-estimate-detail-panel__decision-resubmit"
                  :class="{ 'accruals-estimate-detail-panel__decision-resubmit--selected': resubmitPopoverOpen }"
                  :aria-pressed="resubmitPopoverOpen ? 'true' : 'false'"
                  data-testid="accruals-estimate-detail-panel__request-resubmission"
                  @click="openResubmitPopover"
                >
                  Request for resubmission
                </button>
                <transition name="accruals-resubmit-pop">
                <div
                  v-if="resubmitPopoverOpen"
                  class="accruals-estimate-detail-panel__exclude-popover accruals-estimate-detail-panel__resubmit-popover"
                  role="dialog"
                  aria-label="Request for resubmission"
                  data-testid="accruals-estimate-detail-panel__resubmit-popover"
                >
              <div class="accruals-estimate-detail-panel__exclude-head">
                <p class="accruals-estimate-detail-panel__exclude-title">Request for resubmission</p>
                <button
                  type="button"
                  class="accruals-estimate-detail-panel__close-btn"
                  aria-label="Close request for resubmission"
                  data-testid="accruals-estimate-detail-panel__resubmit-close"
                  @click="closeResubmitPopover"
                >
                  <img
                    :src="assets.ICON_CLOSE"
                    alt=""
                    class="accruals-estimate-detail-panel__close-icon"
                  />
                </button>
              </div>
              <p class="accruals-estimate-detail-panel__exclude-help">
                Tell the preparer what to change. This sends the line back to pending submission.
              </p>
                  <label
                    class="accruals-estimate-detail-panel__exclude-label"
                    for="accruals-estimate-detail-panel__resubmit-reason"
                  >
                    Remarks*
                  </label>
                  <textarea
                    id="accruals-estimate-detail-panel__resubmit-reason"
                    v-model="resubmitReason"
                    class="accruals-estimate-detail-panel__exclude-input"
                    :class="{ 'accruals-estimate-detail-panel__exclude-input--error': Boolean(resubmitError) }"
                    rows="4"
                  />
                  <p
                    v-if="resubmitError"
                    class="accruals-estimate-detail-panel__field-error"
                    role="alert"
                  >
                    {{ resubmitError }}
                  </p>
                  <div class="accruals-estimate-detail-panel__exclude-footer">
                    <button
                      type="button"
                      class="accruals-estimate-detail-panel__exclude-confirm"
                      :class="{ 'accruals-estimate-detail-panel__exclude-confirm--disabled': !resubmitReason.trim() }"
                      :disabled="!resubmitReason.trim()"
                      @click="confirmResubmit"
                    >
                      Submit
                    </button>
                  </div>
                </div>
                </transition>
              </div>
            </div>
            <div class="accruals-estimate-detail-panel__decision-rows">
              <div class="accruals-estimate-detail-panel__decision-row">
                <span>Final amount</span>
                <span class="accruals-estimate-detail-panel__decision-row-value">
                  <span
                    v-if="amountSourceTag"
                    class="accruals-estimate-detail-panel__decision-source"
                  >{{ amountSourceTag }}</span>
                  {{ formatCurrency(confirmedAmount) }}
                </span>
              </div>
              <div class="accruals-estimate-detail-panel__decision-row">
                <span>Debit GL</span>
                <span class="accruals-estimate-detail-panel__decision-row-value">{{ glAccountLabel(debitGl) }}</span>
              </div>
              <div class="accruals-estimate-detail-panel__decision-row">
                <span>Credit GL</span>
                <span class="accruals-estimate-detail-panel__decision-row-value">{{ glAccountLabel(creditGl) }}</span>
              </div>
              <div class="accruals-estimate-detail-panel__decision-row">
                <span>Note to approver</span>
                <span class="accruals-estimate-detail-panel__decision-row-value">
                  {{ preparerNote ? `“${preparerNote}”` : '—' }}
                </span>
              </div>
            </div>
          </section>
        <section
          class="accruals-estimate-detail-panel__po-summary"
          data-testid="accruals-estimate-detail-panel__po-summary"
        >
          <button
            type="button"
            class="accruals-estimate-detail-panel__po-toggle"
            :aria-expanded="poDetailsOpen ? 'true' : 'false'"
            @click="poDetailsOpen = !poDetailsOpen"
          >
            <span>PO Details</span>
            <img
              :src="assets.ESTIMATES_ROW_CHEVRON"
              alt=""
              class="accruals-estimate-detail-panel__po-chevron"
              :class="{ 'accruals-estimate-detail-panel__po-chevron--open': poDetailsOpen }"
              aria-hidden="true"
            />
          </button>
          <div
            v-if="poDetailsOpen"
            class="accruals-estimate-detail-panel__po-grid"
          >
            <div
              v-for="cell in poSummary"
              :key="cell.id"
              class="accruals-estimate-detail-panel__po-cell"
            >
              <span class="accruals-estimate-detail-panel__po-label">{{ cell.label }}</span>
              <span class="accruals-estimate-detail-panel__po-value">{{ cell.value }}</span>
            </div>
          </div>
        </section>

        <div class="accruals-estimate-detail-panel__estimate-row">
        <accruals-estimate-rule-section
          v-if="panelData.ruleSection"
          :section="panelData.ruleSection"
        />

        <section
          v-if="panelData.noaEstimate !== null && panelData.noaEstimate !== undefined"
          class="accruals-estimate-detail-panel__noa-card"
          data-testid="accruals-estimate-detail-panel__noa-card"
        >
          <div class="accruals-estimate-detail-panel__noa-heading">
            <span class="accruals-estimate-detail-panel__noa-label">NOA</span>
            <span
              v-if="confidenceLabel"
              class="accruals-estimate-detail-panel__confidence-badge"
              data-testid="accruals-estimate-detail-panel__confidence-badge"
            >
              {{ confidenceLabel }}
            </span>
          </div>

          <div>
            <p class="accruals-estimate-detail-panel__noa-amount">
              {{ formatCurrency(panelData.noaEstimate) }}
            </p>
            <div class="accruals-estimate-detail-panel__noa-status-row">
              <span class="accruals-estimate-detail-panel__noa-from-label">FROM</span>
              <span class="accruals-estimate-detail-panel__noa-email">
                {{ panelData.irFromEmail || '—' }}
              </span>
            </div>
          </div>

          <div
            v-if="panelData.noaQuote"
            class="accruals-estimate-detail-panel__noa-highlight"
          >
            <span class="accruals-estimate-detail-panel__noa-quote">
              "{{ panelData.noaQuote }}"
            </span>
            <button
              type="button"
              class="accruals-estimate-detail-panel__noa-quote-link"
              aria-label="Open information request"
              data-testid="accruals-estimate-detail-panel__noa-quote-link"
              @click="$emit('view-info-request')"
            >
              <img
                :src="assets.ESTIMATES_EXTERNAL_LINK"
                alt=""
                class="accruals-estimate-detail-panel__noa-quote-link-icon"
                aria-hidden="true"
              />
            </button>
          </div>

          <div class="accruals-estimate-detail-panel__noa-reasoning">
            <span class="accruals-estimate-detail-panel__noa-reasoning-label">NOA Reasoning</span>
            <p class="accruals-estimate-detail-panel__noa-reasoning-text">
              {{ panelData.noaReasoning }}
            </p>
          </div>
        </section>
        </div>

        <div class="accruals-estimate-detail-panel__cards">
          <section
            v-if="showInfoRequestCard"
            class="accruals-estimate-detail-panel__card"
            data-testid="accruals-estimate-detail-panel__info-request-card"
          >
            <div class="accruals-estimate-detail-panel__card-header">
              <h3 class="accruals-estimate-detail-panel__card-title">Information request</h3>
              <div class="accruals-estimate-detail-panel__card-header-right">
                <button
                  type="button"
                  class="accruals-estimate-detail-panel__link-btn"
                  data-testid="accruals-estimate-detail-panel__view-ir-btn"
                  @click="$emit('view-info-request')"
                >
                  View Information Request
                </button>
                <span
                  v-if="panelData.infoRequest === infoRequestTypes.REPLIED"
                  class="accruals-estimate-detail-panel__replied-badge"
                >
                  <img
                    :src="assets.ICON_TICK"
                    alt=""
                    class="accruals-estimate-detail-panel__replied-icon"
                  />
                  Replied
                </span>
              </div>
            </div>

            <div class="accruals-estimate-detail-panel__from-row">
              <span class="accruals-estimate-detail-panel__from-label">From</span>
              <span class="accruals-estimate-detail-panel__from-value">
                {{ panelData.irFromEmail }}
              </span>
            </div>

            <div class="accruals-estimate-detail-panel__quote-box">
              <p class="accruals-estimate-detail-panel__quote-text">
                "{{ panelData.irQuote }}"
              </p>
            </div>
          </section>

          <section
            v-if="showRejectionReason"
            class="accruals-estimate-detail-panel__card accruals-estimate-detail-panel__rejection"
            data-testid="accruals-estimate-detail-panel__rejection-reason"
          >
            <h3 class="accruals-estimate-detail-panel__card-title">Rejection reason</h3>
            <p class="accruals-estimate-detail-panel__quote-text">
              "{{ rejectionReason }}"
            </p>
          </section>

          <section
            v-if="showApprovalReason"
            class="accruals-estimate-detail-panel__card"
            data-testid="accruals-estimate-detail-panel__approval-reason"
          >
            <h3 class="accruals-estimate-detail-panel__card-title">Approval reason</h3>
            <p class="accruals-estimate-detail-panel__quote-text">
              "{{ approvalReason }}"
            </p>
          </section>

          <section
            v-if="preparerNote && !isApproverDecision && !isApprovedDecision"
            class="accruals-estimate-detail-panel__card"
            data-testid="accruals-estimate-detail-panel__preparer-note"
          >
            <h3 class="accruals-estimate-detail-panel__card-title">Note to approver</h3>
            <p class="accruals-estimate-detail-panel__quote-text">
              "{{ preparerNote }}"
            </p>
          </section>

          <div
            v-if="canReview && statusKey === 'pending_submission' && !isPreparerDecision"
            class="accruals-estimate-detail-panel__note-input-wrap"
          >
            <label
              class="accruals-estimate-detail-panel__gl-label"
              for="accruals-estimate-detail-panel__note-input"
            >
              Submission remark
            </label>
            <textarea
              id="accruals-estimate-detail-panel__note-input"
              v-model="noteToApprover"
              rows="2"
              class="accruals-estimate-detail-panel__reason-input"
              placeholder="Add context for the approver (optional)"
              data-testid="accruals-estimate-detail-panel__note-input"
            />
          </div>

          <section
            v-if="showPreparerEditing && showAmountOptions"
            class="accruals-estimate-detail-panel__card"
            data-testid="accruals-estimate-detail-panel__choose-record-card"
          >
            <div class="accruals-estimate-detail-panel__step-header">
              <span class="accruals-estimate-detail-panel__step-indicator">1</span>
              <h3 class="accruals-estimate-detail-panel__step-title">Choose &amp; record</h3>
            </div>

            <div>
              <p class="accruals-estimate-detail-panel__choose-heading">Choose the final amount</p>
              <p class="accruals-estimate-detail-panel__choose-subtitle">
                Pick the basis for this accrual
              </p>
            </div>

            <div class="accruals-estimate-detail-panel__options">
              <button
                v-if="hasRuleEstimate"
                type="button"
                class="accruals-estimate-detail-panel__option"
                :class="{ 'accruals-estimate-detail-panel__option--selected': selectedAmountOption === amountOptions.RULE }"
                data-testid="accruals-estimate-detail-panel__option-rule"
                @click="onSelectAmountOption(amountOptions.RULE)"
              >
                <div>
                  <span class="accruals-estimate-detail-panel__option-label">Use rule</span>
                  <p
                    v-if="panelData.winningRuleLabel"
                    class="accruals-estimate-detail-panel__option-caption"
                    data-testid="accruals-estimate-detail-panel__option-rule-name"
                  >
                    {{ panelData.winningRuleLabel }}
                  </p>
                  <p class="accruals-estimate-detail-panel__option-amount">
                    {{ formatCurrency(panelData.ruleEstimate) }}
                  </p>
                </div>
                <span
                  v-if="selectedAmountOption === amountOptions.RULE"
                  class="accruals-estimate-detail-panel__option-radio"
                >
                  <img
                    :src="assets.ICON_CHECK_WHITE"
                    alt=""
                    class="accruals-estimate-detail-panel__option-radio-icon"
                  />
                </span>
              </button>

              <button
                v-if="hasNoaEstimate"
                type="button"
                class="accruals-estimate-detail-panel__option"
                :class="{ 'accruals-estimate-detail-panel__option--selected': selectedAmountOption === amountOptions.NOA }"
                data-testid="accruals-estimate-detail-panel__option-noa"
                @click="onSelectAmountOption(amountOptions.NOA)"
              >
                <div>
                  <div class="accruals-estimate-detail-panel__option-label-row">
                    <span class="accruals-estimate-detail-panel__option-label">Use Noa</span>
                    <span
                      v-if="suggestedOption === amountOptions.NOA"
                      class="accruals-estimate-detail-panel__suggested-badge"
                    >SUGGESTED</span>
                  </div>
                  <p class="accruals-estimate-detail-panel__option-amount">
                    {{ formatCurrency(panelData.noaEstimate) }}
                  </p>
                </div>
                <span
                  v-if="selectedAmountOption === amountOptions.NOA"
                  class="accruals-estimate-detail-panel__option-radio"
                >
                  <img
                    :src="assets.ICON_CHECK_WHITE"
                    alt=""
                    class="accruals-estimate-detail-panel__option-radio-icon"
                  />
                </span>
              </button>

              <button
                type="button"
                class="accruals-estimate-detail-panel__option accruals-estimate-detail-panel__option--custom"
                :class="{ 'accruals-estimate-detail-panel__option--selected': selectedAmountOption === amountOptions.CUSTOM }"
                data-testid="accruals-estimate-detail-panel__option-custom"
                @click="onSelectAmountOption(amountOptions.CUSTOM)"
              >
                <div>
                  <span class="accruals-estimate-detail-panel__option-label">Custom Estimate</span>
                  <input
                    v-if="selectedAmountOption === amountOptions.CUSTOM"
                    v-model="customAmount"
                    type="text"
                    class="accruals-estimate-detail-panel__custom-input"
                    placeholder="e.g., 500,000.00"
                    :disabled="!canReview"
                    data-testid="accruals-estimate-detail-panel__custom-amount-input"
                    @click.stop
                  />
                  <p
                    v-else
                    class="accruals-estimate-detail-panel__option-placeholder"
                  >
                    e.g., 500,000.00
                  </p>
                </div>
              </button>
            </div>

            <v-btn
              v-if="canReview"
              class="accruals-estimate-detail-panel__approve-btn primaryBtn"
              block
              data-testid="accruals-estimate-detail-panel__approve-btn"
              @click="onApprove"
            >
              <img
                :src="assets.ICON_APPROVE"
                alt=""
                class="accruals-estimate-detail-panel__approve-icon"
              />
              Submit for approval
            </v-btn>
          </section>
        </div>
      </template>

      <accruals-estimate-comments-tab
        v-else-if="activeTab === 'comments'"
        ref="commentsTab"
        :items="commentFeed"
        :loading="activityLoading"
        :draft="commentDraft"
        :sending="commentSending"
        :initials="currentUserInitials"
        :can-compose="canAddComments"
        @update:draft="commentDraft = $event"
        @send="onSendComment"
      />

      <accruals-estimate-activity-tab
        v-else
        :items="activityTimeline"
        :loading="activityLoading"
        :empty-message="activityEmptyMessage"
        :draft="commentDraft"
        :sending="commentSending"
        :initials="currentUserInitials"
        :can-compose="false"
        @update:draft="commentDraft = $event"
        @send="onSendComment"
      />
        </div>
        <div
          v-if="footerActions.length"
          class="accruals-estimate-detail-panel__footer"
          data-testid="accruals-estimate-detail-panel__footer"
        >
          <div
            v-if="showReasonForm"
            class="accruals-estimate-detail-panel__reason-form"
            data-testid="accruals-estimate-detail-panel__reason-form"
          >
            <label
              class="accruals-estimate-detail-panel__reason-label"
              for="accruals-estimate-detail-panel__reason-input"
            >
              {{ reasonMode === 'resubmission' ? 'Reason for resubmission' : 'Rejection reason' }}
            </label>
            <textarea
              id="accruals-estimate-detail-panel__reason-input"
              v-model="reasonText"
              rows="2"
              class="accruals-estimate-detail-panel__reason-input"
              :class="{ 'accruals-estimate-detail-panel__reason-input--error': Boolean(reasonError) }"
              placeholder="Tell the preparer what to fix (min 3 characters)"
              data-testid="accruals-estimate-detail-panel__reason-input"
            />
            <p
              v-if="reasonError"
              class="accruals-estimate-detail-panel__reason-error"
              role="alert"
              data-testid="accruals-estimate-detail-panel__reason-error"
            >
              {{ reasonError }}
            </p>
          </div>
          <div class="accruals-estimate-detail-panel__footer-btns">
            <base-button
              v-if="showReasonForm"
              small
              class="shadow-off"
              data-testid="accruals-estimate-detail-panel__reason-cancel-btn"
              @click="cancelReason"
            >
              Cancel
            </base-button>
            <base-button
              v-if="showReasonForm"
              small
              primary
              class="shadow-off primaryBtn"
              data-testid="accruals-estimate-detail-panel__reason-confirm-btn"
              @click="confirmReason"
            >
              {{ reasonMode === 'resubmission' ? 'Yes, request' : 'Confirm' }}
            </base-button>
            <template v-else>
              <base-button
                v-for="footerAction in footerActions"
                :key="footerAction.id"
                small
                :primary="Boolean(footerAction.primary)"
                class="shadow-off accruals-estimate-detail-panel__footer-btn"
                :class="{ primaryBtn: Boolean(footerAction.primary) }"
                :data-testid="`accruals-estimate-detail-panel__footer-${footerAction.id}-btn`"
                @click="onFooterAction(footerAction.id)"
              >
                {{ footerAction.label }}
              </base-button>
            </template>
          </div>
        </div>
    </aside>
  </div>
  </transition>
</template>

<script>
import AccrualsEstimateRuleSection from './AccrualsEstimateRuleSection.vue';
import AccrualsEstimateActivityTab from './AccrualsEstimateActivityTab.vue';
import AccrualsEstimateCommentsTab from './AccrualsEstimateCommentsTab.vue';
import { ACCRUALS_ASSETS } from '../accrualsPageConstants';
import { mapGetters } from 'vuex';
import {
  ACCRUALS_ACTIVITY_SYSTEM_USER_ID,
  ACCRUALS_COMMENT_TAG,
  ACCRUALS_ESTIMATE_STATUS,
  ACCRUALS_ESTIMATE_STATUS_CONFIG,
  ACCRUALS_ESTIMATE_INFO_REQUEST,
  ACCRUALS_ESTIMATE_NOA_CONFIDENCE_CONFIG,
  ACCRUALS_ESTIMATE_DETAIL_PANEL_TABS,
  ACCRUALS_ESTIMATE_ERRORS,
  ACCRUALS_ESTIMATE_AMOUNT_OPTION,
  ACCRUALS_WORKBENCH_ROLES,
  activityFiles,
  activityInitials,
  activityLogMatchesLine,
  activityRoleLabel,
  activityTimelineSentence,
  activityUserDisplayName,
  activityUserEmail,
  activityUserId,
  buildCommentFeed,
  buildLocationMasterMap,
  findEstimateLineInRows,
  formatGlOptionLabel,
  getEstimateDetailPanelData,
  mapEstimateApiToRow,
  resolveEstimateDisplayLocation,
  isWorkbenchGlDue,
  validateCustomAmount,
  validateGlPair,
  validatePreparerNote,
  validateRejectReason,
  workbenchColumnStatusKey,
} from '../accrualsEstimatesConstants';

export default {
  name: 'AccrualsEstimateDetailPanel',
  components: {
    AccrualsEstimateActivityTab,
    AccrualsEstimateCommentsTab,
    AccrualsEstimateRuleSection,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    row: {
      type: Object,
      default: null,
    },
    canReview: {
      type: Boolean,
      default: false,
    },
    canUnlock: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    role: {
      type: String,
      default: '',
    },
    canApprove: {
      type: Boolean,
      default: false,
    },
    projectId: {
      type: String,
      default: '',
    },
    statusUpdating: {
      type: Boolean,
      default: false,
    },
    detailRefreshKey: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      assets: ACCRUALS_ASSETS,
      amountOptions: ACCRUALS_ESTIMATE_AMOUNT_OPTION,
      infoRequestTypes: ACCRUALS_ESTIMATE_INFO_REQUEST,
      activeTab: 'details',
      selectedAmountOption: ACCRUALS_ESTIMATE_AMOUNT_OPTION.RULE,
      customAmount: '',
      debitGl: '',
      creditGl: '',
      showGlErrors: false,
      noteToApprover: '',
      reasonMode: '',
      reasonText: '',
      reasonError: '',
      lastFocused: null,
      workflowLogs: [],
      activityLoading: false,
      panelLoading: false,
      detailRow: null,
      commentDraft: '',
      commentSending: false,
      pendingComments: [],
      excludePopoverOpen: false,
      excludeReason: '',
      excludeError: '',
      includePopoverOpen: false,
      includeReason: '',
      includeError: '',
      rejectPopoverOpen: false,
      resubmitPopoverOpen: false,
      resubmitReason: '',
      resubmitError: '',
      submitAttempted: false,
      poDetailsOpen: false,
    };
  },
  computed: {
    ...mapGetters('tasks', ['getUserMap']),
    panelData() {
      return getEstimateDetailPanelData(this.detailRow || this.row);
    },
    showShimmer() {
      return this.statusUpdating || this.panelLoading;
    },
    effectivePanelRow() {
      return this.detailRow || this.row || {};
    },
    locationMasterMap() {
      const master = this.$store.state.m_financialclose?.accruals?.locationMaster
        || this.$store.state.accruals?.locationMaster
        || [];
      return buildLocationMasterMap(master);
    },
    headerLocation() {
      const masterMap = this.locationMasterMap;
      return resolveEstimateDisplayLocation(this.detailRow, masterMap)
        || resolveEstimateDisplayLocation(this.row, masterMap)
        || '';
    },
    headerCaption() {
      const row = this.effectivePanelRow;
      const description = row.description || row.raw?.description || row.raw?.item_description || '';
      const trimmed = String(description).trim();
      return trimmed && trimmed !== '—' ? trimmed : '';
    },
    isMutedHeaderRef() {
      return /^(V|G)-/i.test(String(this.panelData?.poNumber || ''));
    },
    userMap() {
      return this.getUserMap || {};
    },
    confidenceLabel() {
      const config = ACCRUALS_ESTIMATE_NOA_CONFIDENCE_CONFIG[this.panelData?.noaConfidence];
      return config?.label?.toUpperCase() || '';
    },
    showInfoRequestCard() {
      const type = this.panelData?.infoRequest;
      return type && type !== ACCRUALS_ESTIMATE_INFO_REQUEST.NONE;
    },
    hasRuleEstimate() {
      return this.panelData?.ruleEstimate !== null && this.panelData?.ruleEstimate !== undefined;
    },
    hasNoaEstimate() {
      return this.hasSelectableAmount(this.panelData?.noaEstimate);
    },
    noaOptionDisabled() {
      return !this.hasSelectableAmount(this.panelData?.noaEstimate);
    },
    showAmountOptions() {
      return this.statusKey !== 'approved' && this.statusKey !== 'excluded';
    },
    suggestedOption() {
      if (this.hasNoaEstimate) return ACCRUALS_ESTIMATE_AMOUNT_OPTION.NOA;
      if (this.hasRuleEstimate) return ACCRUALS_ESTIMATE_AMOUNT_OPTION.RULE;
      return ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM;
    },
    workbenchStatus() {
      return workbenchColumnStatusKey(this.detailRow || this.row);
    },
    isAddGl() {
      return isWorkbenchGlDue(this.row);
    },
    isRecorded() {
      return this.workbenchStatus === ACCRUALS_ESTIMATE_STATUS.RECORDED;
    },
    confirmedAmount() {
      if (this.panelData?.preparerAmount != null && this.panelData.preparerAmount !== '') {
        return this.panelData.preparerAmount;
      }
      return this.panelData?.amount;
    },
    normalizedRole() {
      const role = String(this.role || '').toLowerCase();
      if (role === ACCRUALS_WORKBENCH_ROLES.APPROVER) return role;
      if (role === ACCRUALS_WORKBENCH_ROLES.MANAGER) return role;
      if (role === ACCRUALS_WORKBENCH_ROLES.PREPARER) return role;
      return this.canApprove && !this.canReview
        ? ACCRUALS_WORKBENCH_ROLES.APPROVER
        : ACCRUALS_WORKBENCH_ROLES.PREPARER;
    },
    isApprover() {
      return this.normalizedRole === ACCRUALS_WORKBENCH_ROLES.APPROVER;
    },
    isReviewer() {
      return this.isApprover || this.normalizedRole === ACCRUALS_WORKBENCH_ROLES.MANAGER;
    },
    isPreparerDecision() {
      if (!this.canReview) return false;
      if (this.statusKey === ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION
        || this.statusKey === ACCRUALS_ESTIMATE_STATUS.REJECTED) {
        return true;
      }
      return this.normalizedRole === ACCRUALS_WORKBENCH_ROLES.PREPARER
        && this.statusKey === ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL;
    },
    showPreparerEditing() {
      return this.canReview
        && !this.isPreparerDecision
        && !this.isApproverDecision
        && !this.isApprovedDecision
        && this.statusKey !== ACCRUALS_ESTIMATE_STATUS.EXCLUDED;
    },
    isApproverDecision() {
      return this.statusKey === ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL
        && !this.isPreparerDecision;
    },
    canAddComments() {
      return this.statusKey !== ACCRUALS_ESTIMATE_STATUS.APPROVED;
    },
    isApprovedDecision() {
      return this.statusKey === ACCRUALS_ESTIMATE_STATUS.APPROVED
        && (this.isReviewer || this.canUnlock);
    },
    submitErrors() {
      if (!this.submitAttempted) {
        return { amount: '', debit: '', credit: '', note: '' };
      }
      const gl = validateGlPair(this.debitGl, this.creditGl);
      const customInvalid = this.selectedAmountOption === this.amountOptions.CUSTOM
        && validateCustomAmount(this.customAmount);
      return {
        amount: this.selectedAmountOption ? (customInvalid || '') : ACCRUALS_ESTIMATE_ERRORS.ESTIMATE_REQUIRED,
        debit: gl.debit,
        credit: gl.credit || gl.note,
        note: validatePreparerNote(this.noteToApprover),
      };
    },
    amountSourceTag() {
      const source = String(this.panelData?.amountSource || '').toLowerCase();
      if (source === this.amountOptions.NOA) return 'NOA';
      if (source === this.amountOptions.CUSTOM) return 'CUSTOM';
      if (source === this.amountOptions.RULE) return 'RULE';
      return '';
    },
    statusKey() {
      return workbenchColumnStatusKey(this.row);
    },
    showIncludeAction() {
      return this.canReview && this.statusKey === ACCRUALS_ESTIMATE_STATUS.EXCLUDED;
    },
    showExcludeAction() {
      if (this.statusKey === ACCRUALS_ESTIMATE_STATUS.APPROVED
        || this.statusKey === ACCRUALS_ESTIMATE_STATUS.EXCLUDED) {
        return false;
      }
      return this.canReview || this.isReviewer;
    },
    drawerHeading() {
      const vendor = this.panelData?.vendor || 'Estimate details';
      const po = this.panelData?.poNumber ? ` · ${this.panelData.poNumber}` : '';
      return `${vendor}${po}`;
    },
    poSummary() {
      const row = this.row || {};
      const raw = row.raw || {};
      return [
        { id: 'reference', label: 'Reference', value: this.panelData?.poNumber || row.lineKey || '—' },
        { id: 'location', label: 'Location', value: this.headerLocation || '—' },
        { id: 'open-po', label: 'Open PO value', value: this.formatCurrency(raw.open_po_value ?? row.openPoValue) },
        { id: 'rule', label: 'Rule est.', value: this.formatCurrency(this.panelData?.ruleEstimate) },
        { id: 'noa', label: 'Noa est.', value: this.formatCurrency(this.panelData?.noaEstimate) },
        { id: 'final', label: 'Final amount', value: this.formatCurrency(this.confirmedAmount) },
      ];
    },
    storeGlOptions() {
      return this.$store.state.accruals?.glOptions
        || this.$store.state.m_financialclose?.accruals?.glOptions
        || [];
    },
    glDropdownItems() {
      const options = (this.storeGlOptions || []).map((row) => formatGlOptionLabel(row));
      [this.debitGl, this.creditGl].forEach((value) => {
        const label = formatGlOptionLabel(value);
        if (label && !options.includes(label)) options.push(label);
      });
      return options;
    },
    glValidation() {
      return validateGlPair(this.debitGl, this.creditGl);
    },
    rejectionReason() {
      const row = this.detailRow || this.row || {};
      const raw = row.raw || {};
      const reason = raw.rejected_reason
        || raw.rejection_reason
        || raw.reject_reason
        || row.rejectionReason
        || row.rejectedReason
        || '';
      return String(reason).trim();
    },
    showRejectionReason() {
      return this.statusKey === ACCRUALS_ESTIMATE_STATUS.REJECTED && Boolean(this.rejectionReason);
    },
    approvalReason() {
      const row = this.detailRow || this.row || {};
      const raw = row.raw || {};
      const stored = [
        raw.approver_note,
        raw.approval_reason,
        raw.approved_reason,
        row.approverNote,
      ].map((value) => String(value || '').trim()).find(Boolean);
      if (stored) return stored;
      const events = [
        ...(Array.isArray(raw.workflow_events) ? raw.workflow_events : []),
        ...(this.workflowLogs || []),
      ];
      for (let index = events.length - 1; index >= 0; index -= 1) {
        const entry = events[index] || {};
        const type = String(entry.type || '');
        if (type !== 'approved' && type !== 'bulk_approved') continue;
        const comment = String(
          entry.comment || entry.reason || entry.meta?.comment || entry.meta?.reason || '',
        ).trim();
        if (comment) return comment;
      }
      return '';
    },
    showApprovalReason() {
      return this.statusKey === ACCRUALS_ESTIMATE_STATUS.APPROVED && Boolean(this.approvalReason);
    },
    preparerNote() {
      const row = this.row || {};
      const raw = row.raw || {};
      const note = raw.note_to_approver
        || raw.preparer_note
        || row.noteToApprover
        || '';
      return String(note).trim();
    },
    showReasonForm() {
      return false;
    },
    activityEmptyMessage() {
      if (this.isApprover && this.statusKey === ACCRUALS_ESTIMATE_STATUS.APPROVED) {
        return 'Approved — nothing left to review on this estimate.';
      }
      if (this.isApprover && this.statusKey === ACCRUALS_ESTIMATE_STATUS.REJECTED) {
        return 'Rejected — the preparer has the reason and the line is back with them.';
      }
      return '';
    },
    detailTabs() {
      return ACCRUALS_ESTIMATE_DETAIL_PANEL_TABS.map((tab) => {
        if (tab.id !== 'comments') return tab;
        if (this.showShimmer) return { ...tab, count: null };
        return { ...tab, count: this.commentCount };
      });
    },
    savedCommentFeed() {
      return buildCommentFeed(this.activityEntries).filter((item) => {
        if (item.tag === ACCRUALS_COMMENT_TAG.REJECTION) {
          return this.statusKey === ACCRUALS_ESTIMATE_STATUS.REJECTED;
        }
        if (item.tag === ACCRUALS_COMMENT_TAG.APPROVAL) {
          return this.statusKey === ACCRUALS_ESTIMATE_STATUS.APPROVED;
        }
        return true;
      });
    },
    commentFeed() {
      return [...this.savedCommentFeed, ...(this.pendingComments || [])];
    },
    commentCount() {
      return this.commentFeed.length;
    },
    activityTimeline() {
      return [...this.activityEntries].reverse();
    },
    currentUserInitials() {
      const user = this.$store.state.user || {};
      const name = activityUserDisplayName(user) || user.firstname || user.name || user.email || 'You';
      return activityInitials(name);
    },
    footerActions() {
      const actions = [];
      if (this.canApprove) {
        if (this.statusKey === ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL) {
          actions.push({ id: 'approve', label: 'Approve', primary: true });
          actions.push({ id: 'reject', label: 'Reject' });
        }
      }
      if (this.canReview) {
        if (this.statusKey === ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION
          || this.statusKey === ACCRUALS_ESTIMATE_STATUS.REJECTED) {
          actions.push({ id: 'submit', label: 'Submit for approval', primary: true });
        }
      }
      if (this.isPreparerDecision) {
        return actions.filter((action) => action.id !== 'submit' && action.id !== 'exclude');
      }
      if (this.isApproverDecision) {
        return actions.filter((action) => action.id !== 'approve' && action.id !== 'reject');
      }
      return actions;
    },
    activityEntries() {
      const workflow = (this.workflowLogs || []).map((entry, index) => (
        this.toActivityEntry({
          id: `workflow-${entry._id || index}`,
          text: entry.text || entry.message || 'Workflow update',
          at: entry.created_at || entry.createdAt,
          detail: entry.detail || '',
          userId: activityUserId(entry.userid || entry.created_by),
          userName: activityUserDisplayName(entry.userid_detail),
          userRecord: entry.userid_detail,
          type: entry.type,
          from: entry.from || entry.meta?.from || '',
          actorRole: entry.actor_role || entry.meta?.actor_role || '',
          comment: entry.comment || entry.reason || entry.meta?.comment || entry.meta?.reason || '',
          files: activityFiles(entry),
        }, `workflow-${entry._id || index}`)
      ));
      const skipLocalIds = new Set();
      const noteCommentCounts = new Map();
      (this.workflowLogs || []).forEach((entry) => {
        const type = String(entry.type || '');
        const text = String(entry.text || '').toLowerCase();
        if (type.includes('rule_calculated') || text.includes('rule estimate calculated')) {
          skipLocalIds.add('rule');
        }
        if (type.includes('confirmed') || text.includes('confirmed')) skipLocalIds.add('confirmed');
        if (type.includes('gl_assigned') || text.includes('recorded')) skipLocalIds.add('recorded');
        if (type.includes('unlocked') || text.includes('unlocked')) skipLocalIds.add('unlocked');
        if (
          type.includes('im_created')
          || text.includes('information request created')
          || text.includes('information request replied')
        ) {
          skipLocalIds.add('im');
          skipLocalIds.add('created');
        }
        if (type.includes('schedule_prepared') || text.includes('prepared schedule')) {
          skipLocalIds.add('created');
        }
        if (type === 'excluded' || type === 'bulk_excluded' || text.includes('excluded')) {
          skipLocalIds.add('excluded');
        }
        if (type === 'reincluded' || text.includes('included back') || text.includes('re-included')) {
          skipLocalIds.add('reincluded');
        }
        const savedComment = String(entry.comment || entry.meta?.comment || '').trim();
        if (savedComment && String(entry.type || '') !== 'note_added') {
          skipLocalIds.add(`saved-comment:${savedComment}`);
        }
        if (savedComment && String(entry.type || '') === 'note_added') {
          noteCommentCounts.set(savedComment, (noteCommentCounts.get(savedComment) || 0) + 1);
        }
      });
      const local = (this.panelData?.activityItems || [])
        .filter((item) => !skipLocalIds.has(item.id))
        .filter((item) => {
          const comment = String(item.comment || '').trim();
          if (String(item.type || '') === 'note_added') {
            const remaining = noteCommentCounts.get(comment) || 0;
            if (comment && remaining > 0) {
              noteCommentCounts.set(comment, remaining - 1);
              return false;
            }
            return true;
          }
          return !skipLocalIds.has(`saved-comment:${comment}`);
        })
        .map((item, index) => this.toActivityEntry(item, `local-${item.id || index}`));
      const seen = new Map();
      const entries = [...workflow, ...local]
        .filter((item) => item.at)
        .sort((a, b) => new Date(a.at) - new Date(b.at))
        .filter((item) => {
          const key = item.type === 'note_added'
            ? `note|${item.id}|${item.at || ''}|${item.comment || ''}`
            : `${item.text}|${item.dateKey}|${item.detail || ''}`;
          const existing = seen.get(key);
          if (!existing) {
            seen.set(key, item);
            return true;
          }
          if ((!existing.userName || existing.initials === '?') && item.userName) {
            existing.userName = item.userName;
            existing.initials = item.initials;
          }
          if (!existing.comment && item.comment) existing.comment = item.comment;
          if (!existing.type && item.type) existing.type = item.type;
          return false;
        });
      const note = this.preparerNote;
      if (note && !entries.some((item) => String(item.comment || '').trim() === note)) {
        const target = [...entries].reverse().find((item) => (
          ['submitted', 'bulk_confirmed', 'estimate_confirmed', 'estimate_reconfirmed'].includes(item.type)
          || /estimate confirmed/i.test(item.text)
        ));
        if (target) {
          target.comment = note;
          if (!target.type) target.type = 'estimate_confirmed';
        }
      }
      return entries;
    },
  },
  watch: {
    row: {
      immediate: true,
      handler(next, prev) {
        const sameLine = Boolean(next)
          && Boolean(prev)
          && String(next.estimateId || next._id) === String(prev.estimateId || prev._id)
          && String(next.lineKey || '') === String(prev.lineKey || '');
        if (!sameLine) {
          this.resetPanelState();
          if (this.visible) this.loadPanelDetail();
        }
        this.syncGlFromRow();
      },
    },
    visible(isVisible) {
      if (!isVisible) {
        this.resetPanelState();
        this.restoreFocus();
        return;
      }
      this.lastFocused = document.activeElement || null;
      this.loadPanelDetail();
      this.$store.dispatch('accruals/loadGlOptions');
      this.$nextTick(() => this.focusSheet());
    },
    detailRefreshKey() {
      if (this.visible) this.loadPanelDetail();
    },
  },
  
  beforeDestroy() {
    this.activeTab = 'details';
    this.selectedAmountOption = ACCRUALS_ESTIMATE_AMOUNT_OPTION.RULE;
    this.customAmount = '';
    this.debitGl = '';
    this.creditGl = '';
    this.showGlErrors = false;
    this.noteToApprover = '';
    this.reasonMode = '';
    this.reasonText = '';
    this.reasonError = '';
    this.lastFocused = null;
    this.workflowLogs = [];
    this.activityLoading = false;
    this.panelLoading = false;
    this.detailRow = null;
    this.commentDraft = '';
    this.commentSending = false;
    this.pendingComments = [];
  },
  methods: {
    openExcludePopover() {
      this.excludePopoverOpen = !this.excludePopoverOpen;
      this.excludeError = '';
    },
    confirmExclude() {
      const error = validateRejectReason(this.excludeReason);
      this.excludeError = error;
      if (error) return;
      const reason = String(this.excludeReason).trim();
      this.excludePopoverOpen = false;
      this.excludeReason = '';
      this.$emit('exclude-from-cycle', { row: this.row, reason });
    },
    openIncludePopover() {
      this.includePopoverOpen = !this.includePopoverOpen;
      this.includeError = '';
    },
    confirmInclude() {
      const error = validateRejectReason(this.includeReason);
      this.includeError = error;
      if (error) return;
      const reason = String(this.includeReason).trim();
      this.includePopoverOpen = false;
      this.includeReason = '';
      this.$emit('include-in-cycle', { row: this.row, reason });
    },
    resetPanelState() {
      this.activeTab = 'details';
      this.selectedAmountOption = this.suggestedOption;
      this.customAmount = '';
      this.debitGl = '';
      this.creditGl = '';
      this.showGlErrors = false;
      this.noteToApprover = '';
      this.reasonMode = '';
      this.reasonText = '';
      this.reasonError = '';
      this.workflowLogs = [];
      this.activityLoading = false;
      this.panelLoading = false;
      this.detailRow = null;
      this.commentDraft = '';
      this.commentSending = false;
      this.pendingComments = [];
      this.excludePopoverOpen = false;
      this.excludeReason = '';
      this.excludeError = '';
      this.rejectPopoverOpen = false;
      this.resubmitPopoverOpen = false;
      this.resubmitReason = '';
      this.resubmitError = '';
      this.submitAttempted = false;
      this.poDetailsOpen = this.isApprovedDecision;
    },
    syncGlFromRow() {
      this.debitGl = this.panelData?.debitGl || '';
      this.creditGl = this.panelData?.creditGl || '';
      this.showGlErrors = false;
    },
    toActivityEntry(item, fallbackId) {
      const at = item?.at;
      const date = at ? new Date(at) : null;
      const valid = date && !Number.isNaN(date.getTime());
      const dateKey = valid
        ? (this.$nova?.formatDateTime
          ? this.$nova.formatDateTime(date, 'YYYY-MM-DD')
          : date.toISOString().slice(0, 10))
        : 'unknown';
      const dateLabel = valid
        ? (this.$nova?.formatDateTime
          ? this.$nova.formatDateTime(date, 'D MMM YYYY')
          : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }))
        : '';
      const timeLabel = valid
        ? (this.$nova?.formatDateTime
          ? this.$nova.formatDateTime(date, 'h:mm A')
          : date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }))
        : '';
      const commentTime = valid
        ? (this.$nova?.formatDateTime
          ? this.$nova.formatDateTime(date, 'MMM D · HH:mm')
          : `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`)
        : '';
      const timestamp = valid
        ? (this.$nova?.formatDateTime
          ? this.$nova.formatDateTime(date, 'DD MMM YYYY · HH:mm')
          : this.formatActivityTime(at))
        : '—';
      const person = this.resolveActivityPerson(item);
      const sentence = activityTimelineSentence(item, person.name);
      const remark = String(item?.comment || '').trim();
      const text = remark && !sentence.includes(remark) ? `${sentence} — ${remark}` : sentence;
      return {
        id: item?.id || fallbackId,
        text,
        detail: item?.detail || '',
        type: item?.type || '',
        from: item?.from || '',
        comment: item?.comment || '',
        files: activityFiles(item),
        at,
        dateKey,
        dateLabel,
        timeLabel,
        commentTime,
        timestamp,
        userName: person.name,
        email: person.email,
        role: activityRoleLabel(item),
        initials: activityInitials(person.name),
        userId: activityUserId(item?.userId),
      };
    },
    resolveActivityPerson(item) {
      const userId = activityUserId(item?.userId);
      const users = this.userMap || {};
      const matched = (userId && (users[userId] || users[String(userId)]))
        || Object.values(users).find((entry) => activityUserId(entry?._id) === String(userId))
        || null;
      const record = item?.userRecord && typeof item.userRecord === 'object' ? item.userRecord : matched;
      const session = this.$store.state.user || {};
      const sessionId = activityUserId(session._id || session.id);
      const sessionName = sessionId && sessionId === String(userId)
        ? activityUserDisplayName(session)
        : '';
      const name = activityUserDisplayName(item?.userName)
        || activityUserDisplayName(record)
        || sessionName
        || (userId === ACCRUALS_ACTIVITY_SYSTEM_USER_ID ? 'System User' : '');
      return {
        name,
        email: activityUserEmail(record) || activityUserEmail(matched),
      };
    },
    async ensureUserMasterLoaded() {
      if (Object.keys(this.userMap).length) return;
      try {
        await this.$store.dispatch('tasks/fetchFormDataV3');
      } catch (error) {
        console.log('[AccrualsEstimateDetailPanel]: ensureUserMasterLoaded failed', error);
      }
    },
    async loadPanelDetail({ silent = false } = {}) {
      const estimateId = this.row?.estimateId || this.row?.raw?._id || this.row?._id;
      const projectId = this.projectId || this.$route?.params?.id || '';
      if (!this.visible || !estimateId || !projectId) {
        this.panelLoading = false;
        this.$emit('refresh-settled');
        return;
      }
      const requestId = (this.detailRequestId || 0) + 1;
      this.detailRequestId = requestId;
      if (!silent) this.panelLoading = true;
      try {
        await this.ensureUserMasterLoaded();
        await this.$store.dispatch('accruals/loadLocationMaster').catch(() => []);
        const data = await this.$store.dispatch('accruals/fetchEstimateDetail', {
          projectId,
          estimateId,
          lineKey: this.row?.lineKey || '',
        });
        if (requestId !== this.detailRequestId) return;
        const mapped = mapEstimateApiToRow(data?.row);
        let line = findEstimateLineInRows(mapped ? [mapped] : [], this.row) || null;
        if (line) {
          const masterMap = this.locationMasterMap;
          const display = resolveEstimateDisplayLocation(line, masterMap)
            || resolveEstimateDisplayLocation(this.row, masterMap);
          if (display) line = { ...line, location: display };
        }
        this.detailRow = line;
        const logs = data?.activity || [];
        this.workflowLogs = (line?.lineKey || line?.poNumber || this.row?.lineKey || this.row?.poNumber)
          ? logs.filter((entry) => activityLogMatchesLine(entry, line || this.row))
          : logs;
        if (line) this.$emit('line-updated', line);
        console.log('[AccrualsEstimateDetailPanel]:', {
          action: 'loaded-detail',
          comments: this.commentFeed.length,
          activity: this.workflowLogs.length,
        });
      } catch (error) {
        if (requestId !== this.detailRequestId) return;
        console.log('[AccrualsEstimateDetailPanel]: loadPanelDetail failed', error);
        this.detailRow = null;
        await this.loadWorkflowActivity();
      } finally {
        if (requestId === this.detailRequestId) {
          if (!silent) this.panelLoading = false;
          this.$emit('refresh-settled');
        }
      }
    },
    async loadWorkflowActivity() {
      const resourceId = this.row?.estimateId || this.row?.raw?._id || this.row?._id;
      if (!resourceId) return;
      this.activityLoading = true;
      try {
        await this.ensureUserMasterLoaded();
        const result = await this.$store.dispatch('m_financialclose/fetchActivityLog', {
          resourceId,
        });
        const logs = result?.data || [];
        this.workflowLogs = (this.row?.lineKey || this.row?.poNumber)
          ? logs.filter((entry) => activityLogMatchesLine(entry, this.row))
          : logs;
        console.log('[AccrualsEstimateDetailPanel]:', {
          action: 'loaded-activity',
          count: this.workflowLogs.length,
        });
      } catch (error) {
        console.log('[AccrualsEstimateDetailPanel]: loadWorkflowActivity failed', error);
        this.$store.commit('sbError', 'Could not load activity. Try again.', { root: true });
        this.workflowLogs = [];
      } finally {
        this.activityLoading = false;
      }
    },
    async onSendComment() {
      const note = String(this.commentDraft || '').trim();
      const estimateId = this.row?.estimateId || this.row?.raw?._id || this.row?._id;
      const projectId = this.projectId || this.$route?.params?.id || '';
      if (!note || !estimateId || !projectId || this.commentSending) return;
      const pendingId = `pending-comment-${Date.now()}`;
      const user = this.$store.state.user || {};
      const name = activityUserDisplayName(user) || user.firstname || user.name || user.email || 'You';
      const now = new Date();
      this.pendingComments = [
        ...this.pendingComments,
        {
          kind: 'comment',
          id: pendingId,
          name,
          initials: activityInitials(name),
          time: this.$nova?.formatDateTime
            ? this.$nova.formatDateTime(now, 'MMM D · HH:mm')
            : `${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`,
          tag: null,
          body: note,
          pending: true,
        },
      ];
      this.commentDraft = '';
      this.commentSending = true;
      this.$nextTick(() => {
        const tab = this.$refs.commentsTab;
        if (tab && typeof tab.scrollToLatest === 'function') tab.scrollToLatest();
      });
      try {
        await this.$store.dispatch('accruals/addEstimateNote', {
          projectId,
          items: [{
            estimate_id: estimateId,
            line_key: this.row?.lineKey || null,
            note,
          }],
        });
        const savedBefore = this.savedCommentFeed.filter((entry) => (
          entry.kind === 'comment' && entry.body === note
        )).length;
        await this.loadPanelDetail({ silent: true });
        const savedAfter = this.savedCommentFeed.filter((entry) => (
          entry.kind === 'comment' && entry.body === note
        )).length;
        if (savedAfter > savedBefore) {
          this.pendingComments = this.pendingComments.filter((item) => item.id !== pendingId);
        }
        console.log('[AccrualsEstimateDetailPanel]:', { action: 'comment-sent', rowId: estimateId });
      } catch (error) {
        this.pendingComments = this.pendingComments.filter((item) => item.id !== pendingId);
        this.commentDraft = note;
        console.log('[AccrualsEstimateDetailPanel]: onSendComment failed', error);
      } finally {
        this.commentSending = false;
      }
    },
    statusLabel(status) {
      return ACCRUALS_ESTIMATE_STATUS_CONFIG[status]?.label || status || '—';
    },
    formatActivityTime(value) {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatCurrency(value) {
      if (!this.hasSelectableAmount(value)) return '—';
      const numeric = Number(String(value).replace(/[^0-9.-]/g, ''));
      const rounded = Math.round(numeric);
      return `$${rounded.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    },
    hasSelectableAmount(value) {
      if (value === null || value === undefined || value === '' || value === '—') return false;
      const numeric = Number(String(value).replace(/[^0-9.-]/g, ''));
      return Number.isFinite(numeric);
    },
    glAccountLabel(value) {
      return formatGlOptionLabel(value) || value || '—';
    },
    onSelectAmountOption(option) {
      if (!this.canReview || this.isAddGl || this.isRecorded) return;
      if (option === this.amountOptions.NOA && this.noaOptionDisabled) return;
      this.selectedAmountOption = option;
    },
    onApprove() {
      if (!this.canReview || this.isAddGl || this.isRecorded) return;
      if (this.selectedAmountOption === this.amountOptions.NOA && this.noaOptionDisabled) {
        this.selectedAmountOption = this.suggestedOption;
      }
      this.submitAttempted = true;
      const errors = this.submitErrors;
      if (errors.amount || errors.debit || errors.credit || errors.note) return;
      console.log('[AccrualsEstimateDetailPanel]:', {
        action: 'submit-for-approval',
        rowId: this.row?._id,
        option: this.selectedAmountOption,
        customAmount: this.customAmount,
      });
      this.$emit('submit', {
        row: this.row,
        option: this.selectedAmountOption,
        customAmount: this.customAmount,
        note: this.noteToApprover,
        debitGl: this.debitGl,
        creditGl: this.creditGl,
      });
    },
    openRejectPopover() {
      this.rejectPopoverOpen = !this.rejectPopoverOpen;
      this.reasonMode = 'reject';
      this.reasonError = '';
    },
    openResubmitPopover() {
      this.resubmitPopoverOpen = !this.resubmitPopoverOpen;
      this.resubmitError = '';
      if (this.resubmitPopoverOpen) this.poDetailsOpen = true;
    },
    closeResubmitPopover() {
      this.resubmitPopoverOpen = false;
      this.resubmitError = '';
    },
    confirmResubmit() {
      const error = validateRejectReason(this.resubmitReason);
      this.resubmitError = error;
      if (error) return;
      const reason = String(this.resubmitReason).trim();
      this.resubmitPopoverOpen = false;
      this.resubmitReason = '';
      this.$emit('request-resubmission', { row: this.row, reason });
    },
    onClose() {
      console.log('[AccrualsEstimateDetailPanel]:', { action: 'close', rowId: this.row?._id });
      this.$emit('close');
    },
    focusSheet() {
      const root = this.$refs.sheetBody || this.$el;
      if (!root || typeof root.querySelector !== 'function') return;
      const target = root.querySelector(
        '[data-testid="accruals-estimate-detail-panel__close-btn"], button, input, textarea, select, [tabindex]',
      );
      if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
    },
    restoreFocus() {
      const target = this.lastFocused;
      this.lastFocused = null;
      if (target && typeof target.focus === 'function' && document.contains(target)) {
        target.focus({ preventScroll: true });
      }
    },
    trapTabKey(event) {
      if (event.key !== 'Tab') return;
      const root = this.$refs.sheetBody || this.$el;
      if (!root || typeof root.querySelectorAll !== 'function') return;
      const focusable = Array.from(root.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    onFooterAction(actionId) {
      console.log('[AccrualsEstimateDetailPanel]:', {
        action: 'footer-action',
        actionId,
        rowId: this.row?._id,
      });
      if (actionId === 'approve') {
        this.$emit('approve-estimate', { row: this.row, approver: true });
        return;
      }
      if (actionId === 'submit') {
        this.$emit('submit', {
          row: this.row,
          option: this.selectedAmountOption,
          customAmount: this.customAmount,
          note: this.noteToApprover,
          debitGl: this.debitGl,
          creditGl: this.creditGl,
        });
        return;
      }
      if (actionId === 'reject' || actionId === 'resubmission') {
        this.reasonMode = actionId === 'resubmission' ? 'resubmission' : 'reject';
        this.reasonText = '';
        this.reasonError = '';
        return;
      }
      if (actionId === 'exclude') {
        this.openExcludePopover();
        return;
      }
      if (actionId === 'include') {
        this.openIncludePopover();
      }
    },
    cancelReason() {
      this.reasonMode = '';
      this.reasonText = '';
      this.reasonError = '';
    },
    confirmReason() {
      const error = validateRejectReason(this.reasonText);
      this.reasonError = error;
      if (error) return;
      const reason = String(this.reasonText).trim();
      const event = this.reasonMode === 'resubmission' ? 'request-resubmission' : 'reject-estimate';
      console.log('[AccrualsEstimateDetailPanel]:', {
        action: 'reason-confirmed',
        mode: this.reasonMode,
        rowId: this.row?._id,
      });
      this.$emit(event, { row: this.row, reason });
      this.cancelReason();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AccrualsEstimateDetailPanel.scss';
</style>
